<script setup lang="ts">
import type { BecomeAuthorData } from '~/utils/crystallize/become-author'
import { getCtaProps } from '~/utils/crystallize/cta'

const props = defineProps<{
  data: NonNullable<BecomeAuthorData['components']>['publications']
}>()

const cta = computed(() => getCtaProps(props.data?.cta))
</script>

<template>
  <section v-if="data" class="py-10 md:py-20 lg:py-40">
    <ContentGrid class="gap-y-10 md:gap-y-20 lg:gap-y-40">
      <section v-if="cta.image || cta.title || cta.text" class="full-width-content grid grid-cols-subgrid items-center">
        <div data-image class="hidden md:block col-span-2 lg:col-span-3 2xl:col-span-4 2xl:-mx-[--gutter] relative">
          <Shape variant="circle" class="z-[1] absolute hidden lg:block fill-moss-200 h-full" />
          <CrystallizeImage
            v-if="cta.image"
            :image="cta.image"
            img-class="object-center object-cover aspect-square w-full rounded-full"
          />
        </div>

        <div class="flex flex-col gap-l col-span-4 md:col-span-2 lg:col-span-5 2xl:col-start-6">
          <h2 v-if="cta.title" class="title-l" v-text="cta.title" />

          <div v-if="cta.text" class="flex flex-col gap-s">
            <CrystallizeRichText v-for="(block, key) in cta.text" :key="key" :block="block" class="text-lg" />
          </div>
        </div>
      </section>

      <section v-if="data.publications?.length" data-publications class="full-width-content grid grid-cols-subgrid gap-y-10 lg:gap-y-24">
        <!-- TODO: See if we're able to use clip-path directly on the below div instead of using Shapes -->
        <div v-if="data.title" class="col-span-4 md:col-span-3 lg:col-span-6 2xl:col-span-8 h-20 flex items-center [&>*]:h-full z-[1]">
          <Shape class="fill-blurple-200" variant="hexagon" split="vertical" />
          <div class="flex items-center justify-center sm:justify-start max-h-20 grow bg-blurple-200">
            <h2 class="title-l !font-light overflow-hidden whitespace-nowrap" v-text="data.title" />
          </div>
          <Shape class="fill-blurple-200 rotate-180" variant="hexagon" split="vertical" />
        </div>
        <div class="hidden md:flex justify-between gap-[--gutter]">
          <Shape data-circle variant="circle" class="fill-moss-200 size-20 [--offset:-150%]" />
          <Shape data-circle variant="circle" class="fill-moss-200 size-20 lg:hidden [--offset:-300%]" />
        </div>
        <Shape data-circle variant="circle" class="fill-moss-200 size-20 hidden lg:block [--offset:-300%]" />

        <ul class="grid grid-cols-subgrid col-span-full gap-y-[--gutter]">
          <li v-for="(pub, index) in data.publications" :key="index" class="col-span-4 md:col-span-2 lg:col-span-4 2xl:col-span-5 pl-l pr-m border-l-2 border-moss-300 flex flex-col gap-5">
            <h3 class="title-l" v-text="pub.title.text" />

            <CrystallizeRichText v-for="(block, key) in pub.body.json" :key="key" :block="block" class="text-lg" />
          </li>
        </ul>
      </section>
    </ContentGrid>
  </section>
</template>

<style scoped>
[data-image] {
  --offset: calc(-100% - var(--gutter));

  svg {
    left: var(--offset);
  }

  /* Make the image slide in if the browser supports it and the user is ok with motion */
  @supports (animation-timeline: view()) {
    @media (prefers-reduced-motion: no-preference) {
      picture {
        view-timeline-name: --image-slide;
        view-timeline-axis: block;

        :deep(img) {
          animation: slide-in 1 both;
          animation-timeline: --image-slide;
          animation-range: cover 0% cover 50%;
        }
      }
    }
  }
}

[data-publications] {
  --starting-opacity: 1;

  @supports (animation-timeline: view()) {
    @media (prefers-reduced-motion: no-preference) {
      view-timeline-name: --circle-slide;
      view-timeline-axis: block;

      [data-circle] {
        animation: slide-in 1 both;
        animation-timeline: --circle-slide;
        animation-range: cover 0% cover 50%;
      }
    }
  }
}

@keyframes slide-in {
  from {
    opacity: var(--starting-opacity, 0);
    transform: translateX(var(--offset));
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}
</style>
